import * as Sentry from '@sentry/react'
import type { RootOptions } from 'react-dom/client'

/**
 * Error reporting to Sentry. Only production builds with VITE_SENTRY_DSN report (the deploy sets
 * it from SENTRY_APP_DSN), so local development never uses the monthly allowance. Errors only:
 * no performance tracing or session replays, and none of the personal data the SDK would collect
 * by default (user details and IP address, cookies, headers, bodies, query strings, variables).
 */
const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined

export const sentryEnabled = Boolean(dsn) && import.meta.env.PROD

export function initSentry() {
  if (!sentryEnabled) return

  Sentry.init({
    dsn,
    environment: (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) || 'production',
    release: (import.meta.env.VITE_SENTRY_RELEASE as string | undefined) || undefined,
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: false,
      httpBodies: [],
      urlQueryParams: false,
      stackFrameVariables: false,
    },
    // Not ours: scripts in-app browsers inject into the page (Facebook's iabjs://, and ones that
    // expect jQuery; the app never uses $).
    denyUrls: [/^iabjs:\/\//],
    ignoreErrors: [/^\$ is not defined$/, /^jQuery is not defined$/],
    // Requests to ad and analytics services carry visitor and click IDs in their URLs: leave them
    // out of the trail that comes with an error. The API's own requests stay.
    beforeBreadcrumb: (crumb) =>
      (crumb.category === 'fetch' || crumb.category === 'xhr') && isThirdParty(crumb.data?.url)
        ? null
        : scrubBreadcrumb(crumb),
    beforeSend: (event) => (isThirdPartyNetworkFailure(event) ? null : scrubEvent(event)),
  })
}

interface ReportedError {
  exception?: { values?: { value?: string; stacktrace?: { frames?: { filename?: string }[] } }[] }
}

const failedFetch =
  /^(?:Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.?)(?: \(([^)]+)\))?$/

const tagsAndExtensions = /\/gtag\/js|\/gtm\.js|fbevents\.js|^(?:chrome|moz|safari(?:-web)?)-extension:/

/**
 * "Failed to fetch (ad.doubleclick.net)": an ad blocker, a browser extension or the network stopped
 * a request that the ad and analytics tags make. Not ours to fix, so not reported. Failed requests
 * to the API (or with no host, from our own code) are still reported.
 */
export function isThirdPartyNetworkFailure(event: ReportedError): boolean {
  return (event.exception?.values ?? []).some((error) => {
    const match = failedFetch.exec(error.value ?? '')
    if (!match) return false
    const host = match[1]
    const fromTags = (error.stacktrace?.frames ?? []).some((frame) =>
      tagsAndExtensions.test(frame.filename ?? ''),
    )
    return (host !== undefined && isThirdParty(`https://${host}/`)) || fromTags
  })
}

/**
 * A public share link keeps its secret in the URL fragment (#token), and the token is 64 random
 * letters and digits. Neither may reach Sentry: not in a page URL, a transaction name, a
 * navigation breadcrumb or an error message.
 */
const TOKEN_LIKE = /[A-Za-z0-9_-]{40,}/g

export function scrubText(text: string): string {
  return text.replace(/#[^\s"')]*/g, '#[removed]').replace(TOKEN_LIKE, '[token]')
}

export function scrubUrl(url: string): string {
  return scrubText(url.split('#')[0] ?? '')
}

export function scrubBreadcrumb<T extends { message?: string; data?: Record<string, unknown> }>(crumb: T): T {
  if (crumb.message) crumb.message = scrubText(crumb.message)
  if (crumb.data) {
    for (const [key, value] of Object.entries(crumb.data)) {
      if (typeof value === 'string') {
        crumb.data[key] = key === 'url' || key === 'from' || key === 'to' ? scrubUrl(value) : scrubText(value)
      }
    }
  }
  return crumb
}

interface ScrubbableEvent {
  message?: string
  transaction?: string
  request?: { url?: string; headers?: Record<string, string> }
  exception?: { values?: { value?: string }[] }
  breadcrumbs?: { message?: string; data?: Record<string, unknown> }[]
}

export function scrubEvent<T extends ScrubbableEvent>(event: T): T {
  if (event.message) event.message = scrubText(event.message)
  if (event.transaction) event.transaction = scrubUrl(event.transaction)
  if (event.request?.url) event.request.url = scrubUrl(event.request.url)
  if (event.request?.headers) {
    event.request.headers = Object.fromEntries(
      Object.entries(event.request.headers).filter(
        ([name]) => !['referer', 'authorization'].includes(name.toLowerCase()),
      ),
    )
  }
  for (const error of event.exception?.values ?? []) {
    if (error.value) error.value = scrubText(error.value)
  }
  event.breadcrumbs?.forEach((crumb) => scrubBreadcrumb(crumb))
  return event
}

function isThirdParty(url: unknown) {
  if (typeof url !== 'string') return false
  try {
    const host = new URL(url, window.location.href).hostname
    return host !== window.location.hostname && host !== 'arvaseo.com' && !host.endsWith('.arvaseo.com')
  } catch {
    return false
  }
}

/** Errors that escape every error boundary (route errors are reported by RouteErrorBoundary). */
export const rootErrorOptions: RootOptions = sentryEnabled
  ? { onUncaughtError: Sentry.reactErrorHandler() }
  : {}

export function reportError(error: unknown) {
  if (sentryEnabled) Sentry.captureException(error)
}
